import { Dispatch } from 'redux'
import { toast } from 'react-toastify'
const { createLogic } = require('redux-logic')

import { RootState } from '../rootReducer'
import { File } from '../../lib/interfaces/File.interface'
import { Pagination } from '../../lib/interfaces/Pagination.interface'
import { CountByType } from '../../lib/interfaces/CountByType.interface'
import { JobApplicant } from '../../lib/interfaces/JobApplicant.interface'
import { LocationJobPosition } from '../../lib/interfaces/JobPosition.interface'
import { JobApplicantComment } from '../../lib/interfaces/JobApplicantComment.interface'

import {
  actionTypes,
  ApplicationsUpdateAction,
  ApplicationsApproveAction,
  ApplicationsFetchListAction,
  ApplicationsFetchFilesAction,
  ApplicationsUploadFileAction,
  ApplicationsPostCommentAction,
  ApplicationsFetchSummaryAction,
  ApplicationsUpdateStatusAction,
  ApplicationsSetFilterParamsAction,
  ApplicationsFetchApplicationAction,
  ApplicationsConvertToEmployeeAction,
  ApplicationsFetchNextEmployeeIdAction,
  ApplicationsFetchLocationJobPositionsListAction,
  ApplicationsUpdateLocationJobPositionOpenAction
} from './actions'

import {
  fetchApplicationsList,
  fetchApplicationError,
  updateApplicationError,
  approveApplicationError,
  fetchApplicationSuccess,
  fetchApplicationsSummary,
  fetchNextEmployeeIdError,
  updateApplicationSuccess,
  approveApplicationSuccess,
  fetchApplicationFilesError,
  fetchApplicationsListError,
  fetchNextEmployeeIdSuccess,
  uploadApplicationFileError,
  postApplicationCommentError,
  fetchApplicationFilesSuccess,
  fetchApplicationsListSuccess,
  updateApplicationStatusError,
  uploadApplicationFileSuccess,
  fetchApplicationsSummaryError,
  postApplicationCommentSuccess,
  updateApplicationStatusSuccess,
  fetchApplicationsSummarySuccess,
  convertApplicationToEmployeeError,
  fetchLocationJobPositionsListError,
  updateLocationJobPositionOpenError,
  convertApplicationToEmployeeSuccess,
  fetchLocationJobPositionsListSuccess,
  updateLocationJobPositionOpenSuccess,
} from './actions'

import * as api from '../../lib/api/applications'

const get = require('lodash/get')

type OnDoneType = () => void

const onFetchApplicationLogic = createLogic({
  type: actionTypes.APPLICATIONS_FETCH,
  process({ action }: { action: ApplicationsFetchApplicationAction }, dispatch: Dispatch, done: OnDoneType) {
    const { next, applicationId } = action.payload

    api.fetchApplication(applicationId)
      .then((application: JobApplicant) => {
        dispatch(fetchApplicationSuccess(application))
        if (next) { next(undefined, application) }
      })
      .catch(err => {
        dispatch(fetchApplicationError(err))
        if (next) { next(err) }
      })
      .then(done)
  }
})

const onFetchApplicationFilesLogic = createLogic({
  type: actionTypes.APPLICATIONS_FETCH_FILES,
  process({ action }: { action: ApplicationsFetchFilesAction }, dispatch: Dispatch, done: OnDoneType) {
    const { next, applicationId } = action.payload

    api.fetchApplicationFiles(applicationId)
      .then((files: File[]) => {
        dispatch(fetchApplicationFilesSuccess(files, applicationId))
        if (next) { next(undefined, files) }
      })
      .catch(err => {
        dispatch(fetchApplicationFilesError(err))
        if (next) { next(err) }
      })
      .then(done)
  }
})

const onUploadApplicationFileLogic = createLogic({
  type: actionTypes.APPLICATIONS_UPLOAD_FILE,
  process({ action }: { action: ApplicationsUploadFileAction }, dispatch: Dispatch, done: OnDoneType) {
    const { file, next, applicationId } = action.payload
    api.uploadApplicationFile(applicationId, file)
      .then(() => {
        dispatch(uploadApplicationFileSuccess())
        if (next) { next(undefined, file) }
      })
      .catch(err => {
        const errorMessage = get(err, 'response.data.message', 'Error uploading the file. Please try again')
        dispatch(uploadApplicationFileError(errorMessage))
        toast.error(errorMessage)

        if (next) { next(err) }
      })
      .then(done)
  }
})

const onFetchJobLocationPositionsListLogic = createLogic({
  type: actionTypes.APPLICATIONS_FETCH_LOCATION_JOB_POSITIONS_LIST,
  process({ action }: { action: ApplicationsFetchLocationJobPositionsListAction }, dispatch: Dispatch, done: OnDoneType) {
    api.getJobLocationPositions()
      .then((locationJobPositions: LocationJobPosition[]) => {
        dispatch(fetchLocationJobPositionsListSuccess(locationJobPositions))

        if (action.payload.next) {
          action.payload.next()
        }
      })
      .catch(err => {
        dispatch(fetchLocationJobPositionsListError(err))

        if (action.payload.next) {
          action.payload.next()
        }
      })
      .then(done)
  }
})

const onUpdateJobLocationPositionOpenLogic = createLogic({
  type: actionTypes.APPLICATIONS_UPDATE_LOCATION_JOB_POSITION_OPEN,
  process({ action }: { action: ApplicationsUpdateLocationJobPositionOpenAction }, dispatch: Dispatch, done: OnDoneType) {
    const { next, open, locationJobPositionId } = action.payload

    const apiMethod = open ? api.openPosition : api.closePosition
    apiMethod(locationJobPositionId)
     .then(() => {
       dispatch(updateLocationJobPositionOpenSuccess(locationJobPositionId, open))
       toast.success(`Position successfully ${open ? 'opened' : 'closed'}.`)
       if (next) {
         next()
       }
     })
     .catch(err => {
       dispatch(updateLocationJobPositionOpenError(err))
       toast.error(`Error ${open ? 'opening' : 'closing'} position.`)
       if (next) {
         next()
       }
     })
     .then(done)
  }
})

const onSetApplicationFilterParamsLogic = createLogic({
  type: actionTypes.APPLICATIONS_SET_FILTER_PARAMS,
  process({ action }: { action: ApplicationsSetFilterParamsAction }, dispatch: Dispatch, done: OnDoneType) {
    dispatch(fetchApplicationsList())
    dispatch(fetchApplicationsSummary())
    done()
  }
})

const onFetchApplicationsListLogic = createLogic({
  type: actionTypes.APPLICATIONS_FETCH_LIST,
  process({ action, getState }: { action: ApplicationsFetchListAction, getState: () => RootState }, dispatch: Dispatch, done: OnDoneType) {
    const { next } = action.payload

    const filterParams = getState().applications.filterParams
    api.fetchApplicationsList(filterParams)
      .then((paginatedApplications: Pagination<JobApplicant>) => {
        dispatch(fetchApplicationsListSuccess(paginatedApplications))
        if (next) { next(undefined, paginatedApplications) }
      })
      .catch(err => {
        dispatch(fetchApplicationsListError(err))
        if (next) { next(err) }
      })
      .then(done)
  }
})

const onFetchApplicationsSummaryLogic = createLogic({
  type: actionTypes.APPLICATIONS_FETCH_SUMMARY,
  process({ action, getState }: { action: ApplicationsFetchSummaryAction, getState: () => RootState }, dispatch: Dispatch, done: OnDoneType) {
    const { next } = action.payload

    const filterParams = getState().applications.filterParams
    api.fetchApplicationsSummary(filterParams)
      .then((summary: CountByType[]) => {
        dispatch(fetchApplicationsSummarySuccess(summary))
        if (next) {
          next(undefined, summary)
        }
      })
      .catch(err => {
        dispatch(fetchApplicationsSummaryError(err))
        if (next) {
          next(err)
        }
      })
      .then(done)
  }
})

const onPostApplicationCommentLogic = createLogic({
  type: actionTypes.APPLICATIONS_POST_COMMENT,
  process({ action }: { action: ApplicationsPostCommentAction }, dispatch: Dispatch, done: OnDoneType) {
    const { next, comment, applicationId } = action.payload

    api.postApplicationComment(applicationId, comment)
      .then((applicationComment: JobApplicantComment) => {
        dispatch(postApplicationCommentSuccess(applicationId, applicationComment))
        if (next) { next(undefined, applicationComment) }
      })
      .catch(err => {
        dispatch(postApplicationCommentError(err))

        const errorMessage = get(err, 'response.data.message', 'Error posting comment. Please try again.')
        toast.error(errorMessage)

        if (next) { next(err) }
      })
      .then(done)
  }
})

const onUpdateApplicationStatusLogic = createLogic({
  type: actionTypes.APPLICATIONS_UPDATE_STATUS,
  process({ action }: { action: ApplicationsUpdateStatusAction }, dispatch: Dispatch, done: OnDoneType) {
    const { next, status, applicationId } = action.payload

    api.updateApplicationStatus(applicationId, status)
      .then((application: JobApplicant) => {
        dispatch(updateApplicationStatusSuccess(application))
        toast.success('Application status successfully updated')
        if (next) { next(undefined, application) }
      })
      .catch(err => {
        const errorMessage = get(err, 'response.data.message', 'Error updating status. Please try again')
        dispatch(updateApplicationStatusError(errorMessage))
        toast.error(errorMessage)

        if (next) { next(err) }
      })
      .then(done)
  }
})

const onApproveApplicationLogic = createLogic({
  type: actionTypes.APPLICATIONS_APPROVE,
  process({ action }: { action: ApplicationsApproveAction }, dispatch: Dispatch, done: OnDoneType) {
    const { next, applicationId } = action.payload

    api.approveApplication(applicationId)
      .then((application: JobApplicant) => {
        dispatch(approveApplicationSuccess(application))
        toast.success('Application successfully approved')
        if (next) { next(undefined, application) }
      })
      .catch(err => {
        const errorMessage = get(err, 'response.data.message', 'Error approving application. Please try again')
        dispatch(approveApplicationError(errorMessage))
        toast.error(errorMessage)

        if (next) { next(err) }
      })
      .then(done)
  }
})

const onConvertApplicantToEmployeeLogic = createLogic({
  type: actionTypes.APPLICATIONS_CONVERT_TO_EMPLOYEE,
  process({ action }: { action: ApplicationsConvertToEmployeeAction }, dispatch: Dispatch, done: OnDoneType) {
    const { next, employeeId, applicationId } = action.payload

    api.convertApplicantToEmployee(applicationId, employeeId)
      .then((application: JobApplicant) => {
        dispatch(convertApplicationToEmployeeSuccess(application))
        toast.success('Applicant successfully converted to employee')

        if (next) { next(undefined, application) }
      })
      .catch(err => {
        const errorMessage = get(err, 'response.data.message', 'Error converting applicant to employee. Please try again')
        dispatch(convertApplicationToEmployeeError(errorMessage))
        toast.error(errorMessage)

        if (next) { next(err) }
      })
      .then(done)
  }
})

const onFetchNextEmployeeIdLogic = createLogic({
  type: actionTypes.APPLICATIONS_FETCH_NEXT_EMPLOYEE_ID,
  process({ action }: { action: ApplicationsFetchNextEmployeeIdAction }, dispatch: Dispatch, done: OnDoneType) {
    const { next } = action.payload

    api.fetchNextEmployeeId()
      .then((nextEmployeeId: string) => {
        dispatch(fetchNextEmployeeIdSuccess(nextEmployeeId))
        if (next) { next(undefined, nextEmployeeId) }
      })
      .catch(err => {
        dispatch(fetchNextEmployeeIdError(err))
        if (next) { next(err) }
      })
      .then(done)
  }
})

const onUpdateApplicationLogic = createLogic({
  type: actionTypes.APPLICATIONS_UPDATE,
  process({ action }: { action: ApplicationsUpdateAction }, dispatch: Dispatch, done: OnDoneType) {
    const { next, application, applicationId } = action.payload

    api.updateApplication(applicationId, application)
      .then((updatedApplication: JobApplicant) => {
        dispatch(updateApplicationSuccess(updatedApplication))
        toast.success('Application successfully updated')
        if (next) { next(undefined, updatedApplication) }
      })
      .catch(err => {
        const errorMessage = get(err, 'response.data.message', 'Error updating application. Please try again')
        dispatch(updateApplicationError(errorMessage))
        if (next) { next(errorMessage) }
      })
      .then(done)
  }
})

export const applicationsLogic = [
  onFetchApplicationLogic,
  onFetchApplicationFilesLogic,
  onUploadApplicationFileLogic,
  onFetchJobLocationPositionsListLogic,
  onUpdateJobLocationPositionOpenLogic,
  onSetApplicationFilterParamsLogic,
  onFetchApplicationsListLogic,
  onFetchApplicationsSummaryLogic,
  onPostApplicationCommentLogic,
  onUpdateApplicationStatusLogic,
  onApproveApplicationLogic,
  onConvertApplicantToEmployeeLogic,
  onFetchNextEmployeeIdLogic,
  onUpdateApplicationLogic
]



// WEBPACK FOOTER //
// ./src/store/applications/logic.ts